import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";
import {
  createLiveStreamStore,
  type LiveStreamSnapshot,
  type LiveStreamStore,
} from "./live-stream";

/**
 * React context + hooks over the live-stream store (`live-stream.ts`). The provider creates one
 * store for the app; the stream client (task 11.3) obtains it with `useLiveStreamStore` and writes
 * to it, while the connection indicator reads a snapshot with `useLiveStream`.
 */

const LiveStreamContext = createContext<LiveStreamStore | null>(null);

export function LiveStreamProvider(props: {
  children: ReactNode;
  /** Allows a test or task 11.3 to inject a pre-seeded store; defaults to a fresh one. */
  store?: LiveStreamStore;
}): ReactNode {
  // Create exactly once. The provider is mounted at the app root, so a module-level singleton is
  // avoided (which would leak between tests) while still being stable across renders.
  const store = props.store ?? defaultStore();
  return <LiveStreamContext.Provider value={store}>{props.children}</LiveStreamContext.Provider>;
}

let singleton: LiveStreamStore | null = null;
function defaultStore(): LiveStreamStore {
  singleton ??= createLiveStreamStore();
  return singleton;
}

/** The store itself, for the stream client (11.3) to write to. */
export function useLiveStreamStore(): LiveStreamStore {
  const store = useContext(LiveStreamContext);
  if (store === null) throw new Error("useLiveStreamStore must be used within <LiveStreamProvider>");
  return store;
}

/** A reactive snapshot of the live-stream store, for read-only consumers (the indicator). */
export function useLiveStream(): LiveStreamSnapshot {
  const store = useLiveStreamStore();
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
